import {Observable, of, Subject} from 'rxjs';
import {delay, mergeMap, take} from 'rxjs/operators';
import type {Feature, GeoMap} from '@/interfaces/geo/GeoMap.interface';
import {CustomObservable} from '@/libraries/observable/CustomObservable';
import MapFeaturesService from '@/services/MapFeatures.service';

declare const Dabaskartes: GeoMap;

export default class MapService {
    public whenMapObjectSelected: Subject<Feature> = new Subject();
    public whenMapObjectUnselected: Subject<Feature> = new Subject();
    private whenMapMoveEnd: Subject<void> = new Subject();
    private static instance: MapService;
    private mapFeaturesService: MapFeaturesService = MapFeaturesService.getInstance();
    private mapMoveInAction: boolean = false;
    private lastRenderedFeatures: { objects: Feature[] } = {objects: []};
    private isNearbyCircleVisible: boolean = false;
    private localPadding: { left: number, duration: number } = {left: 0, duration: 850};
    private localPopupElement?: HTMLElement;
    private localMapElement!: HTMLDivElement;
    private localStartPosition?: { x: number, y: number, zoom: number };

    private constructor() {
        this.subscribeOnEvents();
    }

    public static getInstance(): MapService {
        if (!MapService.instance) {
            MapService.instance = new MapService();
        }
        return MapService.instance;
    }

    public initMap(element: HTMLDivElement, locationWgs?: string): void {
        this.localMapElement = element;
        const locationSplit: string[] = (locationWgs || '').split(';');
        if (locationSplit.length === 3) {
            this.localStartPosition = {
                x: Number(locationSplit[0]),
                y: Number(locationSplit[1]),
                zoom: Number(locationSplit[2]),
            };
        }
    }

    public openPopup(element: HTMLElement): Observable<void> {
        this.localPopupElement = element;
        return this.renderMap();
    }

    public closePopup(): Observable<void> {
        this.localPopupElement = undefined;
        return this.renderMap();
    }

    public renderFeatures(objects: Feature[]): Observable<void> {
        this.lastRenderedFeatures.objects = objects;
        return this.renderMap();
    }

    private renderMap(): Observable<void> {
        return of(null).pipe(
            mergeMap(() => {
                Dabaskartes.renderMap(
                    {
                        features: this.lastRenderedFeatures.objects,
                        layerVisibility: [],
                        allFeatures: this.mapFeaturesService.allFeatures(),
                        selectFeatureById: null,
                        zoomTo: null,
                        mapVisible: true,
                        padding: this.localPadding,
                        startPosition: this.localStartPosition,
                        nearbyCircleVisible: this.isNearbyCircleVisible,
                        filterOptions: null,
                        popupHtml: this.localPopupElement?.outerHTML || undefined,
                        onClickOnFeature: this.handleObjectClick.bind(this),
                        onUnselect: this.handleObjectUnselect.bind(this),
                    },
                    this.localMapElement
                );
                return this.mapReadyForNextRender();
            })
        );
    }

    private handleObjectClick(feature: Feature): void {
        this.whenMapObjectSelected.next(feature);
    }

    private handleObjectUnselect(objectId: number): void {
        const unselectedFeature: Feature | undefined = this.mapFeaturesService
            .allFeatures()
            .find((feature: Feature): boolean => feature.values_.OBJECTID === objectId);
        if (unselectedFeature) {
            this.whenMapObjectUnselected.next(unselectedFeature);
        }
    }

    private subscribeOnEvents(): void {
        document.addEventListener('movestart', (): void => {
            this.mapMoveInAction = true;
        });
        document.addEventListener('moveend', (): void => {
            this.mapMoveInAction = false;
            this.whenMapMoveEnd.next();
        });
    }

    private mapReadyForNextRender(): Observable<void> {
        return CustomObservable.empty().pipe(
            delay(50),
            mergeMap(() => {
                return this.mapMoveInAction
                    ? this.whenMapMoveEnd.pipe(take(1))
                    : CustomObservable.empty();
            }),
        );
    }
}
